import { useState } from "react";
import { useTier } from "@/lib/tierContext";
import { tierRingClass } from "@/components/tiers";

/** Cartoon-style avatar styles we surface in the picker. All free DiceBear
 *  collections — funny + cool, no licensed characters. Order is the order
 *  shown in the picker grid. */
export const AVATAR_STYLES = [
  { id: "avataaars", label: "Cartoon faces" },
  { id: "fun-emoji", label: "Fun emoji" },
  { id: "lorelei", label: "Lorelei" },
  { id: "micah", label: "Micah" },
  { id: "notionists", label: "Notionists" },
  { id: "open-peeps", label: "Open peeps" },
  { id: "personas", label: "Personas" },
  { id: "adventurer", label: "Adventurer" },
  { id: "big-smile", label: "Big smile" },
  { id: "bottts-neutral", label: "Robots" },
  { id: "pixel-art", label: "Pixel art" },
  { id: "thumbs", label: "Thumbs" },
] as const;

export type AvatarStyleId = (typeof AVATAR_STYLES)[number]["id"];

/** Build a DiceBear avatar URL. Same `seed` + `style` is deterministic, so
 *  the same URL always renders the same character — no extra storage. */
export function avatarUrl(style: AvatarStyleId, seed: string): string {
  const s = encodeURIComponent(seed);
  return `https://api.dicebear.com/9.x/${style}/svg?seed=${s}`;
}

function initialsOf(name: string | null | undefined, email: string): string {
  const src = (name || email || "").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

const SIZE_CLASS: Record<NonNullable<AvatarProps["size"]>, string> = {
  xs: "w-6 h-6 text-[9px]",
  sm: "w-8 h-8 text-[11px]",
  md: "w-10 h-10 text-xs",
  lg: "w-16 h-16 text-xl",
};

/** Online-dot sizing, in sync with the avatar size. The dot is anchored to
 *  the bottom-right corner; tiny avatars get a tiny dot, big avatars get a
 *  proportionally bigger one. The ring colour matches the surrounding ink
 *  background so the dot reads as a small inset, not a stuck-on sticker. */
const DOT_CLASS: Record<NonNullable<AvatarProps["size"]>, string> = {
  xs: "w-2 h-2 ring-[1.5px]",
  sm: "w-2.5 h-2.5 ring-2",
  md: "w-3 h-3 ring-2",
  lg: "w-4 h-4 ring-[3px]",
};

interface AvatarProps {
  url?: string | null;
  name?: string | null;
  email?: string | null;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  /** Render the green online dot in the bottom-right corner. Pass the
   *  user's ``is_online`` flag — undefined means "don't show a dot at
   *  all", which is the safe default for surfaces that don't have
   *  presence info loaded. Ignored when ``status`` is set. */
  online?: boolean;
  /** Authentic 3-state presence dot: emerald (active), amber (away), or none
   *  (offline). Takes precedence over ``online`` when provided. */
  status?: "active" | "away" | "offline";
  /** When true, even if a URL is set we still render the initials fallback
   *  underneath — useful while the SVG is fetching for the first time. */
  alt?: string;
  /** Draw the leaderboard tier ring. Pass ``userId`` to look the tier up from
   *  the agency-wide map, or ``tier`` directly where it is already loaded.
   *  Silver and above only — bronze intentionally has no ring. */
  userId?: string | null;
  tier?: string | null;
}

/** Round profile avatar. Renders the cartoon SVG when ``url`` is set,
 *  otherwise the user's initials in an accent-tinted circle. Falls back to
 *  initials too if the image fails to load (DiceBear hiccup, offline). */
export function Avatar({
  url,
  name,
  email,
  size = "md",
  className = "",
  online,
  status,
  alt,
  userId,
  tier,
}: AvatarProps) {
  const [imgErrored, setImgErrored] = useState(false);
  const looked = useTier(userId);
  const ringCls = tierRingClass(tier ?? looked, size);
  const initials = initialsOf(name, email || "");
  const sizeCls = SIZE_CLASS[size];
  const dotCls = DOT_CLASS[size];

  const showImg = !!url && !imgErrored;
  // Presence dot. ``status`` (active/away/offline) wins when provided; else
  // fall back to the legacy boolean ``online``. ``undefined`` on both means
  // "no presence info" — render no dot.
  const effStatus: "active" | "away" | "offline" | undefined =
    status ?? (online === true ? "active" : online === false ? "offline" : undefined);
  const showDot = effStatus === "active" || effStatus === "away";
  const dotColor =
    effStatus === "active" ? "bg-emerald-400" : "bg-amber-400";
  const dotTitle = effStatus === "active" ? "Active" : "Away";

  return (
    <div
      className={`${sizeCls} relative rounded-full flex-shrink-0 ${className}`}
      aria-label={alt || name || email || "avatar"}
    >
      <div
        className={`w-full h-full rounded-full overflow-hidden bg-accent-500/15 text-accent-300 flex items-center justify-center font-bold ${ringCls}`}
      >
        {showImg ? (
          <img
            src={url!}
            alt={alt || name || email || "avatar"}
            className="w-full h-full object-cover bg-white"
            loading="lazy"
            onError={() => setImgErrored(true)}
          />
        ) : (
          <span className="select-none">{initials}</span>
        )}
      </div>
      {showDot && (
        <span
          className={`absolute bottom-0 right-0 ${dotCls} rounded-full ${dotColor} ring-ink-900`}
          title={dotTitle}
          aria-label={dotTitle}
        />
      )}
    </div>
  );
}
